import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { App } from "../../../routes/Constants";

import DataTable from "@/global/tables/DataTable";
import { getViewPatientsColumns } from "./ViewPatientsColumns";
import StyledPatients from "./StyledPatients";
import { StatsCards } from "../Dashboard/components/StatsCards";
import APIVariables from "../../../services/api-variables";
import Loader from "../../common/Loader/Loader";
import Modal from "react-modal";
import StyledModal from "../../common/Modal/StyledModal";
import Inspect from "../../../assets/images/Inspect.svg";
import Bulb from "../../../assets/images/bulb.svg";
import ErrorIcon from "../../../assets/icons/error.svg";

import { handleCatch } from "../../../shared/helpers";
import Axios from "../../../services/Axios";
import useRoles from "../../../hooks/useRoles";
import { Button } from "@/global/base/button";
import ViewPAF from "../../common/ViewPAF/ViewPAF";
import { PafFooterActions } from "../PAF/layout/PafFooterActions";
import PrescriptionReviewModal from "../../common/ViewPAF/components/PrescriptionReviewModal";
import { typeOfPAF } from "../../../constants/typeOfPAF";
import { getHistoryPrescriptionsExcept } from "../PAF/utils/pafPrescriptionHelpers";
import {
  mergePrescriptionListFields,
  normalizePafPrescription,
} from "../../common/ViewPAF/utils/viewPafFormLogic";
import {
  buildPrescriptionEditPayload,
  fetchPrescriptionFieldOptions,
  parsePrescriptionEditError,
  patchPrescriptionEdit,
} from "../../../services/prescriptionEdit";

/** Maps API `patient.patient_type` to the renew-PAF URL segment (same rules as PAF `typeOfForm`). */
function patientTypeToRenewalPathSegment(patientType) {
  if (patientType === "Male") return typeOfPAF.MALE;
  if (patientType === "Woman of Non-Childbearing Potential") return typeOfPAF.WOMANOFNCP;
  return typeOfPAF.WOMANOFCPRENEW2;
}

/** Column ids that have a header filter in the DataTable (must match column id or meta.filterName). */
const TABLE_FILTER_KEYS = [
  "id",
  "prescriber_name",
  "patient_initials",
  "gender",
  "indication",
  "product",
  "status",
  "dob_after",
  "dob_before",
];

/** Labels for filter chips when using table column filters. */
const TABLE_FILTER_LABELS = {
  id: "Patient ID",
  prescriber_name: "Prescriber Name",
  patient_initials: "Initials",
  gender: "Gender",
  indication: "Indication",
  product: "Molecule",
  status: "Status",
  dob_after: "DOB After",
  dob_before: "DOB Before",
};

const STATUS_FROM_LINK = {
  submitted: [
    { id: 0, value: "Submitted", label: "Status" },
    { id: 3, value: "Expiring", label: "Status" },
  ],
  "approved-paf": [
    { id: 1, value: "Approved", label: "Status" },
    { id: 3, value: "Expiring", label: "Status" },
  ],
  "overdue-paf": [{ id: 4, value: "Overdue", label: "Status" }],
  "rejected-paf": [{ id: 5, value: "Rejected", label: "Status" }],
  "dispensed-paf": [{ id: 6, value: "Dispensed", label: "Status" }],
};

function buildPrescriptionsFilterString(filtersMap, searchValue, page, size) {
  let filterString = "?";
  if (searchValue.length > 0) {
    filterString += `search_lookup=${searchValue}`;
  }

  [...filtersMap.keys()].forEach((key) => {
    const currentFilter = filtersMap.get(key);
    currentFilter.forEach((filter) => {
      if (!Boolean(filter.dataType)) {
        filterString += `&${key}=${filter.id}`;
      }
      if (filter.dataType === "date") {
        filterString += `&${key}_after=${filter.after}`;
        filterString += `&${key}_before=${filter.before}`;
      }
      if (filter.dataType === "string") {
        filterString += `&${key}=${filter.value}`;
      }
    });
  });

  filterString += `&page_size=${size}&page=${page + 1}`;
  return filterString;
}

/**
 * Resolves display label for a filter value from API filter_data when available.
 * @param {string} key - filter key (e.g. 'gender', 'status', 'indication', 'product')
 * @param {string} value - value sent to API (e.g. 'm', '0', '1')
 * @param {Object} filterData - res.data.filter_data from /prescriptions
 */
function getFilterDisplayValue(key, value, filterData = {}) {
  const list = filterData[key];
  if (Array.isArray(list)) {
    const item = list.find((i) => String(i.id) === String(value));
    if (item) return item.value;
  }
  return value;
}

/**
 * Converts DataTable's flat filter object into this page's Map format (for getPatients).
 * Preserves existing filters for keys not in TABLE_FILTER_KEYS (e.g. from URL or legacy UI).
 * @param {Object} filterData - res.data.filter_data for display labels and counts
 */
function tableFiltersToLegacyMap(tableFilters, existingFilters, filterData = {}) {
  const next = new Map(existingFilters);
  for (const key of TABLE_FILTER_KEYS) {
    const value = tableFilters?.[key];
    if (value === undefined || value === "") {
      next.delete(key);
    } else {
      const trimmed = String(value).trim();
      next.set(key, [
        {
          dataType: "string",
          value: trimmed,
          label: TABLE_FILTER_LABELS[key] ?? key,
          displayValue: getFilterDisplayValue(key, trimmed, filterData),
        },
      ]);
    }
  }
  return next;
}

function areFilterMapsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.size !== b.size) return false;

  for (const [key, valueA] of a.entries()) {
    if (!b.has(key)) return false;
    const valueB = b.get(key);
    if (JSON.stringify(valueA) !== JSON.stringify(valueB)) return false;
  }

  return true;
}

function hasMeaningfulTableFilters(tableFilters = {}) {
  return Object.entries(tableFilters).some(([key, value]) => {
    if (key === "search") {
      return Boolean(String(value ?? "").trim());
    }
    return value !== undefined && String(value).trim() !== "";
  });
}

const ViewPatients = () => {
  const debounceTime = 500;
  const location = useLocation();
  const [filters, setFilters] = useState(new Map());
  // const [isFetching, setIsFetching] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [isUrlFilterApplied, setIsUrlFilterApplied] = useState(false);
  const [, setShowFilter] = useState("");
  const nhsNoRef = useRef();
  const prescriberNameRef = useRef();
  const nameRef = useRef();
  const genderRef = useRef();
  const moleculeRef = useRef();
  const statusRef = useRef();
  const capsuleRef = useRef();
  const dobRef = useRef();
  const pafRef = useRef();
  const [items, setItems] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]); // selected patient ids for merge
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null); // { success: boolean, kept, deactivated, message }
  const [showMergeResultModal, setShowMergeResultModal] = useState(false);


  const {
    isPrescriber,
    isPharmacy,
    isHomecare,
    isOutpatient,
    storageData,
    isConnectedNurse,
    canReviewPaf,
    canChangePrescription,
  } = useRoles();

  const [pafPreview, setPafPreview] = useState(null);
  const [pafPreviewData, setPafPreviewData] = useState(null);
  const [pafPreviewPatientDetails, setPafPreviewPatientDetails] = useState(null);
  const [pafPreviewPrescriptions, setPafPreviewPrescriptions] = useState([]);
  const [pafPreviewLoading, setPafPreviewLoading] = useState(false);
  const [pafPreviewError, setPafPreviewError] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [pafEditMode, setPafEditMode] = useState(false);
  const [pafEditSaving, setPafEditSaving] = useState(false);
  const [pafEditError, setPafEditError] = useState(null);
  const pafEditFormRef = useRef(null);

  const navigate = useNavigate();

  const controller = useRef(new AbortController());
  const isFetchingPatientsRef = useRef(true);
  const clearTableSelectionRef = useRef(null); // DataTable bulk action clearRows, called when merge modal closes
  const urlFilterAppliedAtRef = useRef(0);

  const [isLoadingTable, setIsLoadingTable] = useState(true);

  const chooseStatusFromLink = useCallback((type) => {
    const preset = STATUS_FROM_LINK[type];
    if (!preset) return;

    urlFilterAppliedAtRef.current = Date.now();
    setFilters((prev) => {
      const next = new Map(prev);
      next.set("status", preset);
      return next;
    });
  }, []);

  const [patientsStatistics, setPatientsStatistics] = useState({});
  const [searchValue, setSearchValue] = useState("");

  /** Present only when the URL contains `?search=` (e.g. navbar); drives table toolbar + API without clobbering in-page typing. */
  const managePatientsSearchFromUrl = useMemo(() => {
    const p = new URLSearchParams(location.search);
    if (!p.has("search")) return undefined;
    return p.get("search") ?? "";
  }, [location.search]);

  const [isLoading, setIsLoading] = useState(true);

  const container = React.useRef(null);


  function handleOutsideClick(event) {
    const { target } = event;
    const refs = [
      nhsNoRef,
      nameRef,
      genderRef,
      moleculeRef,
      statusRef,
      capsuleRef,
      dobRef,
      pafRef,
      prescriberNameRef,
    ];
    let contains = refs.some((ref) => {
      try {
        let svgClass = target?.getAttribute("class");
        if (svgClass === "rs-icon") {
          return true;
        }

        return (
          ref?.current?.contains(target) ||
          target?.className?.includes("wrapper-filter") ||
          target?.className?.includes("rs-calendar") ||
          target?.className?.includes("rs-calendar-header-month-toolbar") ||
          target?.className?.includes("rs-picker-toolbar") ||
          target?.className?.includes("rs-btn")
        );
      } catch (error) {
        return false;
      }
    });
    if (!contains) {
      setShowFilter("");
    }
  }
  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  /* eslint-disable */
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    let isUrlCondition = !isUrlFilterApplied && query.has("status"); ///check if there is url status and filter from url wasn't apply
    const delayDebounce = setTimeout(() => {
      if (isUrlCondition) {
        chooseStatusFromLink(query.get("status"));
        setIsUrlFilterApplied(true);
      } else {
        setPageIndex(0);
        getPatients(0, pageSize);
      }
    }, debounceTime * Boolean(!isUrlCondition)); /// multiply with bool => if !condition don't wait debounceTime, else wait mls = debounceTime
    return () => {
      clearTimeout(delayDebounce);
    };
  }, [filters, searchValue, isUrlFilterApplied]);

  useEffect(() => {
    getStatistics();

    return () => {
      setPatientsStatistics({});
    };
  }, []);

  async function getPatients(page = pageIndex, size = pageSize) {
    setIsLoadingTable(true);
    if (isFetchingPatientsRef.current) {
      controller.current.abort();
      controller.current = new AbortController();
    }
    isFetchingPatientsRef.current = true;

    const filterString = buildPrescriptionsFilterString(filters, searchValue, page, size);

    const link = APIVariables.PRESCRIPTIONS + filterString;
    const res = await Axios.get(link, {
      signal: controller.current.signal,
    }).catch((error) => {
      setIsLoading(false);
      handleCatch(error);
    });

    if (res?.status === 200) {
      setIsLoading(false);
      setItems(res.data.results ?? []);
      setFilterData(res.data.filter_data ?? {});
      setTotalRows(res.data.count ?? res.data.results?.length ?? 0);
    }

    setTimeout(() => {
      isFetchingPatientsRef.current = false;
      setIsLoadingTable(false);
    }, 500);
  }

  const getStatistics = async () => {
    const res = await Axios.get(APIVariables.PATIENT_STATISTICS).catch(
      (error) => {
        handleCatch(error);
      }
    );
    setPatientsStatistics(res.data);
  };

  const openPatientPafPreview = (row) => {
    const formId = row?.prescription?.id;
    const patientId = row?.id;
    if (!formId || !patientId) return;
    setPafPreview({
      prescriptionId: formId,
      patientId,
      prescriptionFromList: row?.prescription ?? null,
    });
  };

  const closePatientPafPreview = useCallback(() => {
    setPafPreview(null);
    setPafPreviewData(null);
    setPafPreviewPatientDetails(null);
    setPafPreviewPrescriptions([]);
    setPafPreviewError(null);
    setPafPreviewLoading(false);
    setPafEditMode(false);
    setPafEditSaving(false);
    setPafEditError(null);
    pafEditFormRef.current = null;
  }, []);

  const viewPafNoop = useCallback(() => { }, []);

  const handlePafEditFormChange = useCallback((form) => {
    pafEditFormRef.current = form;
  }, []);

  const startPafEdit = useCallback(() => {
    setPafEditMode(true);
    setPafEditError(null);
    pafEditFormRef.current = pafPreviewData;
  }, [pafPreviewData]);

  const cancelPafEdit = useCallback(() => {
    setPafEditMode(false);
    setPafEditError(null);
    pafEditFormRef.current = null;
  }, []);

  const savePafEdit = useCallback(async () => {
    if (!pafPreview?.prescriptionId) return;
    const form = pafEditFormRef.current ?? pafPreviewData;
    if (!form) return;

    setPafEditSaving(true);
    setPafEditError(null);

    try {
      const fieldOptions = await fetchPrescriptionFieldOptions(
        form.product_id,
        form.patient?.type
      );
      const payload = buildPrescriptionEditPayload(form, fieldOptions);
      const updated = await patchPrescriptionEdit(
        pafPreview.prescriptionId,
        payload
      );
      setPafPreviewData(
        mergePrescriptionListFields(
          normalizePafPrescription(updated),
          pafPreview?.prescriptionFromList
        )
      );
      setPafEditMode(false);
      pafEditFormRef.current = null;
      await getPatients(pageIndex, pageSize);
    } catch (error) {
      setPafEditError(parsePrescriptionEditError(error));
      handleCatch(error);
    } finally {
      setPafEditSaving(false);
    }
  }, [pafPreview, pafPreviewData, pageIndex, pageSize]);

  const exportPDFWithMethod = (e, patientID) => {
    e.stopPropagation();
    return Axios.get(APIVariables.DOWNLOAD_PAF + patientID)
      .then((response) => response.data.report)
      .then(async (base64) => {
        const base64Pdf = await fetch(`data:application/pdf;base64,${base64}`);
        return base64Pdf.blob();
      })
      .then((blob) => {
        const file = new Blob([blob], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        const downloadLink = document.createElement("a");
        downloadLink.href = fileURL;
        downloadLink.download = `PAF.pdf`;
        downloadLink.click();
        downloadLink.remove();
        URL.revokeObjectURL(fileURL);
      })
      .catch((error) => {
        handleCatch(error);
      });
  };

  const handleServerFilterChange = useCallback((tableFilters) => {
    const hasNonEmptyIncoming = hasMeaningfulTableFilters(tableFilters);
    setFilters((prev) => {
      const isLikelyStartupResetAfterUrlApply =
        isUrlFilterApplied &&
        !hasNonEmptyIncoming &&
        prev.size > 0 &&
        Date.now() - urlFilterAppliedAtRef.current < 3000;

      if (isLikelyStartupResetAfterUrlApply) {
        return prev;
      }

      const next = tableFiltersToLegacyMap(tableFilters, prev, filterData);
      return areFilterMapsEqual(prev, next) ? prev : next;
    });
    if (tableFilters?.search !== undefined) {
      setSearchValue(String(tableFilters.search ?? ""));
    }
    setPageIndex(0);
  }, [filterData]);

  useEffect(() => {
    if (managePatientsSearchFromUrl === undefined) return;
    setSearchValue(managePatientsSearchFromUrl);
  }, [managePatientsSearchFromUrl]);

  useEffect(() => {
    if (!pafPreview?.prescriptionId || !pafPreview?.patientId) return undefined;

    const ac = new AbortController();

    setPafPreviewLoading(true);
    setPafPreviewError(null);
    setPafPreviewData(null);
    setPafPreviewPatientDetails(null);
    setPafPreviewPrescriptions([]);

    (async () => {
      try {
        const [rxRes, patientRes] = await Promise.all([
          Axios.get(APIVariables.PRESCRIPTIONS_GET + pafPreview.prescriptionId, {
            signal: ac.signal,
          }),
          Axios.get(APIVariables.PATIENTS_DETAILS + pafPreview.patientId, {
            signal: ac.signal,
          }),
        ]);

        if (ac.signal.aborted) return;

        if (rxRes?.status !== 200) {
          setPafPreviewError("Could not load this prescription.");
          return;
        }

        setPafPreviewData(
          mergePrescriptionListFields(rxRes.data, pafPreview.prescriptionFromList)
        );

        if (patientRes?.status === 200) {
          const { prescriptions, ...patientInfo } = patientRes.data;
          setPafPreviewPrescriptions(prescriptions ?? []);
          setPafPreviewPatientDetails(patientInfo);
        }
      } catch (e) {
        if (ac.signal.aborted) return;
        handleCatch(e);
        const d = e?.response?.data;
        setPafPreviewError(
          (typeof d === "string" ? d : d?.detail) ||
          "Could not load this prescription."
        );
      } finally {
        if (!ac.signal.aborted) {
          setPafPreviewLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [pafPreview]);

  const pafPreviewHistory = useMemo(
    () =>
      getHistoryPrescriptionsExcept(
        pafPreviewPrescriptions,
        pafPreview?.prescriptionId
      ),
    [pafPreviewPrescriptions, pafPreview?.prescriptionId]
  );

  const pafPreviewPatientDetailsMerged = useMemo(() => {
    const fromPatient = pafPreviewPatientDetails || {};
    const fromRx = pafPreviewData?.patient || {};
    return { ...fromRx, ...fromPatient };
  }, [pafPreviewPatientDetails, pafPreviewData?.patient]);

  const getPafPreviewOpenedItem = useCallback(() => {
    if (!pafPreviewData) return {};
    const status = pafPreviewData.status_name ?? pafPreviewData.status;
    return { ...pafPreviewData, status };
  }, [pafPreviewData]);

  const previewFindProduct = useMemo(() => {
    const productId = pafPreviewData?.product_id;
    if (productId == null || !storageData?.products?.length) return undefined;
    return storageData.products.find((p) => p.product_id === productId);
  }, [pafPreviewData?.product_id, storageData?.products]);

  const pafPreviewFooterIsFormApproved = useMemo(() => {
    if (!pafPreviewData) return null;
    const status = pafPreviewData.status_name ?? pafPreviewData.status;
    if (status === "Rejected") return false;
    if (status === "Approved" || status === "Dispensed") return true;
    return null;
  }, [pafPreviewData]);

  const previewDownloadPafPdf = useCallback(() => {
    const patientId = pafPreview?.patientId;
    if (!patientId) return Promise.resolve();
    const ev = { stopPropagation: () => { } };
    return exportPDFWithMethod(ev, patientId);
  }, [pafPreview?.patientId]);

  const navigateToPafFromPreview = useCallback(
    (stateForNavigate) => {
      const patientId = pafPreview?.patientId;
      const prescriptionId = pafPreview?.prescriptionId;
      if (!patientId || !prescriptionId) return;
      closePatientPafPreview();
      const url =
        App.PRESCRIPTION_AUTHORISATION_FORM_DEFAULT + `${patientId}/${prescriptionId}`;
      if (stateForNavigate == null) {
        navigate(url);
      } else {
        navigate(url, { state: stateForNavigate });
      }
    },
    [pafPreview, closePatientPafPreview, navigate]
  );

  const goToFullPafFromPreview = useCallback(
    () => navigateToPafFromPreview(null),
    [navigateToPafFromPreview]
  );

  const goToFullPafWithAutoApprove = useCallback(
    () =>
      navigateToPafFromPreview({
        fromViewPatientsAutoApprove: true,
        approveAndDispense: false,
      }),
    [navigateToPafFromPreview]
  );

  const goToFullPafWithAutoApproveAndDispense = useCallback(
    () =>
      navigateToPafFromPreview({
        fromViewPatientsAutoApprove: true,
        approveAndDispense: true,
      }),
    [navigateToPafFromPreview]
  );

  const goToFullPafWithAutoReject = useCallback(
    () =>
      navigateToPafFromPreview({
        fromViewPatientsAutoReject: true,
      }),
    [navigateToPafFromPreview]
  );

  const refreshPafPreviewData = useCallback(async () => {
    if (!pafPreview?.prescriptionId) return;
    try {
      const rxRes = await Axios.get(
        APIVariables.PRESCRIPTIONS_GET + pafPreview.prescriptionId
      );
      if (rxRes?.status === 200) {
        setPafPreviewData(
          mergePrescriptionListFields(rxRes.data, pafPreview?.prescriptionFromList)
        );
      }
      await getPatients(pageIndex, pageSize);
    } catch (error) {
      handleCatch(error);
    }
  }, [pafPreview?.prescriptionId, pageIndex, pageSize]);

  const handleReviewSuccess = useCallback(async () => {
    await refreshPafPreviewData();
    setReviewModalOpen(false);
  }, [refreshPafPreviewData]);

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((pid) => pid !== id);
      return [...prev, id];
    });
  };

  const toggleSelectAll = (e) => {
    e.stopPropagation();
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((it) => it.id));
    }
  };

  // open modal for selecting target patient to merge
  const mergeSelected = () => {
    if (selectedIds.length < 2) {
      return alert("Select at least two patients to merge.");
    }
    setShowMergeModal(true);
  };

  const viewPatientsColumns = useMemo(
    () => getViewPatientsColumns(exportPDFWithMethod, filterData, items),
    [exportPDFWithMethod, filterData, items]
  );

  const BulkMergeAction = useCallback(
    function BulkMergeAction({ rows, clearRows }) {
      clearTableSelectionRef.current = clearRows;
      if (!isPharmacy && !isPrescriber) return null;
      return (
        <Button
          variant="complement"
          data-testid="view-patients-merge-button"
          onClick={() => {
            setSelectedIds(rows.map((r) => r.id));
            setShowMergeModal(true);
          }}
        >
          Merge Patients
        </Button>
      );
    },
    [isPharmacy, isPrescriber]
  );

  const closeMergeModal = useCallback(() => {
    setShowMergeModal(false);
    if (clearTableSelectionRef.current) {
      clearTableSelectionRef.current();
      clearTableSelectionRef.current = null;
    }
  }, []);

  const confirmMerge = async () => {
    if (selectedIds.length < 2) {
      setMergeResult({ success: false, message: "Select at least two patients to merge." });
      setShowMergeResultModal(true);
      return;
    }

    setIsMerging(true);
    closeMergeModal();

    try {
      const res = await Axios.post(
        APIVariables.MERGE_PATIENTS || "/patients/merge-patients/",
        selectedIds
      );

      const kept = res?.data?.kept_patient;
      const deactivated = res?.data?.deactivated_patients || [];

      // clear selection and refresh list
      setSelectedIds([]);
      getPatients(pageIndex, pageSize);
      closeMergeModal();

      setMergeResult({
        success: true,
        kept,
        deactivated,
      });
      setShowMergeResultModal(true);
    } catch (error) {
      const serverMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null);

      setMergeResult({
        success: false,
        message: serverMessage || "An unexpected error occurred while merging patients.",
      });
      setShowMergeResultModal(true);
    } finally {
      setIsMerging(false);
    }
  };

  const viewPatientsStatsCards = useMemo(
    () => [
      {
        key: "All Patients",
        title: "All Patients",
        value: patientsStatistics?.all_patients,
        details: "View a list of all patients that have been prescribed",
        onClick: () => {
          setFilters(new Map());
          setSearchValue("");
          setPageIndex(0);
          setIsUrlFilterApplied(false);
          navigate(App.VIEW_PATIENTS, { replace: true });
        },
      },
      {
        key: "Active Patients",
        title: "Active Patients",
        value: patientsStatistics?.active_patients,
        details: "View a list of all currently prescribed patients",
        onClick: () =>
          navigate({
            pathname: App.VIEW_PATIENTS,
            search:
              isOutpatient || isHomecare
                ? "?status=approved-paf"
                : "?status=submitted",
          }),
      },
      {
        key: "Overdue PAF",
        title: "Overdue PAF",
        value: patientsStatistics?.overdue_paf,
        details: "View a list of all PAF's that are overdue",
        onClick: () =>
          navigate({
            pathname: App.VIEW_PATIENTS,
            search: "?status=overdue-paf",
          }),
      },
      {
        key: "Rejected PAF",
        title: isOutpatient || isHomecare ? "Dispensed PAF" : "Rejected PAF",
        value:
          isOutpatient || isHomecare
            ? patientsStatistics?.dispensed_paf
            : patientsStatistics?.rejected_paf,
        details:
          isOutpatient || isHomecare
            ? "View a list of all scripts that have been dispensed"
            : "View a list of all scripts that have been rejected",
        onClick: () =>
          navigate({
            pathname: App.VIEW_PATIENTS,
            search:
              isOutpatient || isHomecare
                ? "?status=dispensed-paf"
                : "?status=rejected-paf",
          }),
      },
    ],
    [
      patientsStatistics,
      navigate,
      isOutpatient,
      isHomecare,
      setFilters,
      setSearchValue,
      setPageIndex,
      setIsUrlFilterApplied,
    ]
  );

  return (
    <StyledPatients data-testid="view-patients-page">
      {isLoading ? (
        <div data-testid="view-patients-loader">
          <Loader />
        </div>
      ) : (
        <div className="px-6 py-4" data-testid="view-patients-content">
          <StatsCards
            cards={viewPatientsStatsCards}
            showDetails={false}
            justify="left"
            testIdPrefix="view-patients"
            className={
              (isPrescriber ? "max-w-325" : "max-w-300")
            }
          />
          <div
            className="rounded-md border bg-white mt-4 px-4"
            data-testid="view-patients-table"
          >
            <DataTable
              columns={viewPatientsColumns}
              data={items}
              tableTitle="Patients"
              syncGlobalFilter={managePatientsSearchFromUrl}
              onRowClick={(row) => openPatientPafPreview(row)}
              BulkActionComponent={BulkMergeAction}
              loading={isLoadingTable}
              getRowTestId={(row) => `view-patients-row-${row?.id}`}
              serverPagination={{
                enabled: true,
                pageIndex,
                pageSize,
                totalRows,
                onPageChange: (newIndex) => {
                  setPageIndex(newIndex);
                  getPatients(newIndex, pageSize);
                },
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPageIndex(0);
                  getPatients(0, newSize);
                },
                onFilterChange: handleServerFilterChange,
              }}
            />
          </div>
          <div id="pdf-element" ref={container} data-testid="view-patients-pdf-element"></div>
          {pafPreview && (
            <StyledModal>
              <Modal
                isOpen
                onRequestClose={closePatientPafPreview}
                style={{
                  content: {
                    top: "10vh",
                    left: "50%",
                    right: "auto",
                    bottom: "auto",
                    transform: "translateX(-50%)",
                    width: "96vw",
                    maxHeight: "85vh",
                    minHeight: "85vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    borderRadius: "12px",
                    padding: "10px 0px 10px 0px",
                  },
                }}
                contentLabel="Prescription authorisation form preview"
                overlayClassName="overlay-modal"
                shouldCloseOnOverlayClick
              >
                <div
                  className="mostly-customized-scrollbar min-h-0 min-w-0 flex-1 overflow-x-hidden"
                  data-testid="view-patients-paf-preview-modal"
                >
                  {pafPreviewLoading ? (
                    <div
                      className="flex justify-center py-16"
                      data-testid="view-patients-paf-preview-loading"
                    >
                      <Loader />
                    </div>
                  ) : null}
                  {pafPreviewError ? (
                    <div
                      className="rounded-md bg-red-50 p-4 text-sm text-red-800"
                      data-testid="view-patients-paf-preview-error"
                    >
                      {pafPreviewError}
                    </div>
                  ) : null}
                  {!pafPreviewLoading && pafPreviewData ? (
                    <div data-testid="view-patients-paf-preview-content">
                    <ViewPAF
                      key={`paf-preview-${pafPreview.prescriptionId}-${pafEditMode ? "edit" : "view"}`}
                      receivedForm={pafPreviewData}
                      patientDetails={pafPreviewPatientDetailsMerged}
                      historyPafs={pafPreviewHistory}
                      isFormApproved={pafPreviewFooterIsFormApproved}
                      isCreateNewPAF={false}
                      isPafEditMode={pafEditMode}
                      onEditFormChange={handlePafEditFormChange}
                      hideEditButton={pafEditMode}
                      onCreateNewCycle={
                        (isPrescriber || isConnectedNurse) &&
                        previewFindProduct?.can_renew_registration === true &&
                        previewFindProduct?.product_active === true
                          ? () => {
                              const patientId = pafPreview?.patientId;
                              const prescriptionId = pafPreview?.prescriptionId;
                              if (!patientId) return;
                              const segment = patientTypeToRenewalPathSegment(
                                pafPreviewData?.patient?.patient_type
                              );
                              closePatientPafPreview();
                              navigate(
                                `${App.PAF_RENEW_DEFAULT}${segment}/${patientId}/${prescriptionId ?? ""}`
                              );
                            }
                          : undefined
                      }
                      setCapsule_changes_tracker={viewPafNoop}
                      capsule_changes_tracker={null}
                      handleChangeSubmitedForm={viewPafNoop}
                      handleAditionalOptionsChange={viewPafNoop}
                      deletePropertyValue={viewPafNoop}
                      footer={
                        <div
                          className="flex flex-col gap-3"
                          data-testid="view-patients-paf-preview-footer"
                        >
                          <PafFooterActions
                            isPrescriber={isPrescriber}
                            isConnectedNurse={isConnectedNurse}
                            findProduct={previewFindProduct}
                            isLastFormApprovedAvailableForRenew={false}
                            getOpenedItem={getPafPreviewOpenedItem}
                            isFormApproved={pafPreviewFooterIsFormApproved}
                            isHomecare={isHomecare}
                            isOutpatient={isOutpatient}
                            isPharmacy={isPharmacy}
                            activePafDetails={pafPreviewData}
                            onNavigateRenewalPaf={() => {
                              const patientId = pafPreview?.patientId;
                              const prescriptionId = pafPreview?.prescriptionId;
                              if (!patientId) return;
                              const segment = patientTypeToRenewalPathSegment(
                                pafPreviewData?.patient?.patient_type
                              );
                              closePatientPafPreview();
                              navigate(
                                `${App.PAF_RENEW_DEFAULT}${segment}/${patientId}/${prescriptionId ?? ""}`
                              );
                            }}
                            onApprove={goToFullPafWithAutoApprove}
                            onApproveAndDispense={goToFullPafWithAutoApproveAndDispense}
                            onReject={goToFullPafWithAutoReject}
                            onRevertToApprover={goToFullPafFromPreview}
                            onDispense={goToFullPafFromPreview}
                            onNavigateFinish={closePatientPafPreview}
                            onDownloadPaf={previewDownloadPafPdf}
                            onEdit={startPafEdit}
                            canChangePrescription={canChangePrescription}
                            isEditMode={pafEditMode}
                            onSaveEdit={savePafEdit}
                            onCancelEdit={cancelPafEdit}
                            isSavingEdit={pafEditSaving}
                            editSaveError={pafEditError}
                            canReviewPaf={canReviewPaf}
                            onReview={() => setReviewModalOpen(true)}
                          />
                        </div>
                      }
                    />
                    </div>
                  ) : null}
                </div>
              </Modal>
              <div data-testid="view-patients-review-modal">
                <PrescriptionReviewModal
                  open={reviewModalOpen}
                  prescriptionId={pafPreview.prescriptionId}
                  onClose={() => setReviewModalOpen(false)}
                  onSuccess={handleReviewSuccess}
                />
              </div>
            </StyledModal>
          )}
          {showMergeModal && (
            <StyledModal>
              <Modal
                isOpen={showMergeModal}
                onRequestClose={closeMergeModal}
                style={{
                  content: {
                    top: "50%",
                    left: "50%",
                    right: "auto",
                    bottom: "auto",
                    marginRight: "-50%",
                    transform: "translate(-50%, -50%)",
                    width: "520px",
                    borderRadius: "12px",
                  },
                }}
                contentLabel="Merge Patients"
                overlayClassName="overlay-modal"
                shouldCloseOnOverlayClick={false}
              >
                <div
                  className="wrapper-modal flex-column-center"
                  data-testid="view-patients-merge-modal"
                >
                  <img
                    src={Inspect}
                    alt="Notify"
                    className="icon-click-notify"
                    data-testid="view-patients-merge-modal-icon"
                  />
                  <div
                    className="second-text"
                    data-testid="view-patients-merge-modal-message"
                  >
                    You have indicated that this patient is a duplicate record. To be considered a potential patient duplicate record,
                    they must have the same <span className="bold">Initials, Date of Birth, Hospital and Patient Category</span> before the system allows you to merge the patient
                  </div>

                  {/* Select target patient for merge */}
                  <div className="mt-4" data-testid="view-patients-merge-ids-section">
                    <div className="mb-3" style={{ fontWeight: 'bold' }}>These patient IDs will be merged:</div>
                    <div
                      style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: 8, marginBottom: 12 }}
                      data-testid="view-patients-merge-ids"
                    >
                      {selectedIds.join(", ")}
                    </div>
                  </div>

                  <div className="mt-4 flex-column-center">
                    <button
                      className="green-button green-haze-background"
                      data-testid="view-patients-merge-confirm"
                      onClick={confirmMerge}
                      disabled={isMerging}
                    >
                      {isMerging ? "Merging..." : "Confirm Merge"}
                    </button>
                    <button
                      className="cancel red-label mt-3"
                      data-testid="view-patients-merge-cancel"
                      onClick={closeMergeModal}
                      disabled={isMerging}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Modal>
            </StyledModal>
          )}

          {showMergeResultModal && (
            <StyledModal>
              <Modal
                isOpen={showMergeResultModal}
                onRequestClose={() => setShowMergeResultModal(false)}
                style={{
                  content: {
                    top: "50%",
                    left: "50%",
                    right: "auto",
                    bottom: "auto",
                    marginRight: "-50%",
                    transform: "translate(-50%, -50%)",
                    width: "520px",
                    borderRadius: "12px",
                  },
                }}
                contentLabel="Merge Result"
                overlayClassName="overlay-modal"
                shouldCloseOnOverlayClick={true}
              >
                <div
                  className="wrapper-modal flex-column-center"
                  data-testid="view-patients-merge-result-modal"
                >
                  <div
                    className="main-text"
                    data-testid={
                      mergeResult?.success
                        ? "view-patients-merge-result-success-icon"
                        : "view-patients-merge-result-error-icon"
                    }
                  >
                    {mergeResult?.success ? <img
                      src={Bulb}
                      alt='Bulb Icon'
                    />
                      :
                      <img
                        src={ErrorIcon}
                        alt="Error Icon"
                      />
                    }
                  </div>

                  <div
                    className="second-text mt-4"
                    style={{ whiteSpace: "pre-wrap" }}
                    data-testid="view-patients-merge-result-message"
                  >
                    {mergeResult?.success ? (
                      <div>
                        <div>These patients have now been merged under: <span className="bold">{mergeResult.kept}</span></div>
                        <div className="mt-4">
                          These patient IDs have been merged:{" "}
                          <div
                            className="mt-2"
                            style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: 8, marginBottom: 12 }}
                            data-testid="view-patients-merge-result-ids"
                          >
                            {[mergeResult.kept, ...mergeResult.deactivated].join(", ")}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>{mergeResult?.message || "An error occurred."}</div>
                    )}
                  </div>
                  <div className="mt-4" style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      className="green-button green-haze-background"
                      data-testid="view-patients-merge-result-close"
                      onClick={() => {
                        setShowMergeResultModal(false);
                        // on success close merge modal already closed; ensure selection cleared
                        if (mergeResult?.success) {
                          setSelectedIds([]);
                        }
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Modal>
            </StyledModal>
          )}
        </div>
      )}
    </StyledPatients>
  );
};
export default ViewPatients;
